import BudgetAccountComparison from './BudgetAccountComparison';
import React, { useMemo, useState } from 'react';
import { decideWorkflowProposals, applyWorkflowProposals, revertWorkflowProposals, runWorkflow } from '../../utils/api';
import { useAssistantContext } from '../../assistant/context';
import MonthlyLandingCard from './MonthlyLandingCard';
import JournalImbalanceCard, { JournalImbalanceDetails } from './JournalImbalanceCard';
import RevenueReforecastCard from './RevenueReforecastCard';
import OperatingBudgetCard from './OperatingBudgetCard';
import FinancialReviewCard from './FinancialReviewCard';
import BudgetReviewCard from './BudgetReviewCard';
import WatchSetup from './WatchSetup';
import { Headline, Caveat, SignalStrip, Section, Sections, ActionCard, Button } from './response/primitives';
import { previewOf } from './response/layoutResponse';
import './WorkflowRunCard.css';

/**
 * Route a typed artifact to its renderer. An unknown type is stated plainly
 * rather than rendered as a blank space — a result the UI cannot draw is a
 * deployment mismatch the user should be able to report.
 */
function ArtifactRenderer({ artifact }) {
  if (artifact.type === 'monthly_landing') return <MonthlyLandingCard artifact={artifact} />;
  if (artifact.type === 'journal_imbalance') return <JournalImbalanceCard artifact={artifact} />;
  if (artifact.type === 'revenue_reforecast') return <RevenueReforecastCard artifact={artifact} />;
  if (artifact.type === 'operating_budget') return <OperatingBudgetCard artifact={artifact} />;
  if (artifact.type === 'forecast_driver_review' || artifact.type === 'forecast_build') return <RevenueReforecastCard artifact={artifact} />;
  if (artifact.type === 'rooms_expense_audit' || artifact.type === 'monthly_close_review') return <FinancialReviewCard artifact={artifact} />;
  if (artifact.type === 'budget_review') return <BudgetReviewCard artifact={artifact} />;
  return (
    <div className="wf-run-empty">
      This run produced a “{artifact.type}” result that this version of the app cannot display.
    </div>
  );
}

/**
 * Renders one agentic workflow run: narrative first, then the evidence it
 * read, then the proposals it produced.
 *
 * This card deliberately does NOT reuse ForecastChangeCard's direct-write
 * handler. That component writes to the Forecast V3 endpoints straight from
 * the browser, which would bypass everything the workflow layer exists for:
 * the append-only run audit, the apply reservation that makes a write
 * exactly-once, and the staleness check that refuses to overwrite a change
 * made since the analysis. Workflow proposals go through /decision and
 * /apply, and nowhere else.
 */

const CONFIDENCE_LABEL = { high: 'High confidence', medium: 'Medium confidence', low: 'Low confidence' };

/**
 * Kinds whose proposedValue is an entry posted BESIDE the target, not a
 * replacement for it. They must never be rendered as "current -> proposed".
 */
const ADDITIVE_KINDS = new Set(['journal_adjustment', 'journal_adjustment_with_entry_type', 'forecast_adjustment']);
const ADVISORY_KINDS = new Set(['savings', 'labor_action', 'accrual', 'data_request']);
const APPLY_RETRY_AFTER_MS = 5 * 60 * 1000;

const isRetryableApplying = proposal => proposal?.state === 'applying'
  && Number.isFinite(Date.parse(proposal.applyStartedAt || ''))
  && Date.now() - Date.parse(proposal.applyStartedAt) >= APPLY_RETRY_AFTER_MS;

const signed = value => `${Number(value) > 0 ? '+' : ''}${Number(value).toLocaleString()}`;

const compactContextLabel = (label) => {
  const seen = new Set();
  return String(label || '').split(' · ').map(part => part.trim()).filter(part => {
    if (!part || seen.has(part)) return false;
    seen.add(part);
    return true;
  }).join(' · ');
};

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const STATE_LABEL = {
  draft: 'Awaiting review',
  reviewed: 'Reviewed',
  approved: 'Approved',
  deferred: 'Deferred',
  applying: 'Applying…',
  applied: 'Applied',
  failed: 'Failed',
  rejected: 'Rejected',
  superseded: 'Superseded',
  reverted: 'Reverted',
};

// An applied change the server knows how to undo (its apply result carried a
// revert plan). Only these get a Revert control; the rest say so in Details.
const isRevertible = proposal => proposal?.state === 'applied'
  && (proposal.revertible === true || !!proposal.after?.revert);

function formatTarget(proposal) {
  const t = proposal.target || {};
  const bits = [];
  if (t.accountNumber || t.accountName) bits.push(`${t.accountNumber || ''} ${t.accountName || ''}`.trim());
  if (t.departmentName) bits.push(t.departmentName);
  if (t.year && t.month) bits.push(`${t.year}-${String(t.month).padStart(2, '0')}`);
  if (proposal.kind === 'forecast_revenue_stat_month' && t.occupiedRooms != null && t.adr != null) {
    bits.push(`${Number(t.importedRoomNights ?? t.occupiedRooms).toLocaleString()} RN @ $${Number(t.adr).toFixed(2)} ADR`);
  }
  return bits.join(' · ');
}


/**
 * The proof line: residual, correcting entry, and the result they produce.
 *
 * An accountant accepts a correction by seeing it prove out to zero. That
 * argument was previously carried three times in prose — the narrative, the
 * artifact's explanation, and the proposal's rationale — and nowhere as
 * arithmetic the reader could check. Here it is the arithmetic, and the prose
 * around it is cut to one line.
 */
function ProofLine({ proposals }) {
  const withImpact = proposals.filter(p => Number.isFinite(p.expectedImpact?.dayNetBefore));
  if (withImpact.length === 0) return null;
  const p = withImpact[0];
  const isCoordinatedJournalRepair = proposals.length > 1
    && proposals.every(item => String(item.kind || '').startsWith('journal_adjustment'))
    && withImpact.length === proposals.length;
  if (withImpact.length !== 1 && !isCoordinatedJournalRepair) return null;
  const before = Number(p.expectedImpact.dayNetBefore);
  const after = Number(isCoordinatedJournalRepair
    ? p.expectedImpact.dayNetAfterAllApproved ?? 0
    : p.expectedImpact.dayNetAfter ?? 0);
  const entry = Number(isCoordinatedJournalRepair
    ? p.expectedImpact.displayedAdjustment ?? -Number(p.expectedImpact.combinedAdjustment)
    : p.proposedValue);
  if (!Number.isFinite(entry)) return null;
  const fmt = (v) => `${v < 0 ? '−' : v > 0 ? '+' : ''}$${Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="wf-proof">
      <span className="wf-proof-term"><span className="wf-proof-label">Imbalance</span><strong className="wf-proof-value wf-proof-value--open">{fmt(before)}</strong></span>
      <span className="wf-proof-operator" aria-hidden="true">+</span>
      <span className="wf-proof-term"><span className="wf-proof-label">Correction</span><strong className="wf-proof-value">{fmt(entry)}</strong></span>
      <span className="wf-proof-operator" aria-hidden="true">=</span>
      <span className="wf-proof-term"><span className="wf-proof-label">{after === 0 ? 'Balanced' : 'Remaining'}</span><strong className={`wf-proof-value wf-proof-value--result ${after === 0 ? 'is-zero' : ''}`}>{fmt(after)}</strong></span>
    </div>
  );
}


const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const amount = (v) => (Number.isFinite(v)
  ? Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })
  : '—');

const variance = (v) => (Number.isFinite(v)
  ? `${v > 0 ? '+' : v < 0 ? '\u2212' : ''}${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  : '—');

/**
 * Many proposals, read as a period ledger.
 *
 * A reforecast is not one decision — it is a change per account per month, and
 * a twelve-month rebuild can carry sixty of them. Rendering one card each turns
 * the review into scrolling, and the question a reviewer actually asks first is
 * "what does this do to each period, and to the total". So the periods are the
 * rows, the accounts underneath them are a disclosure, and the total is proved
 * with the same double rule the single-correction case uses.
 */
function ProposalLedger({ proposals, selected, onToggleMany, disabled }) {
  const [openMonth, setOpenMonth] = useState(null);

  // Months are the rows, because a forecast is read a month at a time. The
  // departments that make up a month are the disclosure: they answer "why did
  // October move", which is only asked once October is seen to have moved.
  const months = useMemo(() => {
    const byMonth = new Map();
    for (const p of proposals) {
      const y = Number(p.target?.year);
      const m = Number(p.target?.month);
      if (!Number.isInteger(y) || !Number.isInteger(m)) continue;
      const key = `${y}-${String(m).padStart(2, '0')}`;
      if (!byMonth.has(key)) {
        byMonth.set(key, { key, year: y, month: m, items: [], start: 0, proposed: 0, startKnown: true });
      }
      const row = byMonth.get(key);
      row.items.push(p);
      if (Number.isFinite(p.currentValue)) row.start += Number(p.currentValue);
      else row.startKnown = false;
      if (Number.isFinite(p.proposedValue)) row.proposed += Number(p.proposedValue);
    }

    // Departments within the month, largest mover first — that ordering is the
    // answer to "what drove it".
    for (const row of byMonth.values()) {
      const byDept = new Map();
      for (const p of row.items) {
        const name = p.target?.accountName
          || String(p.title || '').split('—')[0].trim()
          || 'Other';
        if (!byDept.has(name)) byDept.set(name, { name, items: [], start: 0, proposed: 0, startKnown: true });
        const dept = byDept.get(name);
        dept.items.push(p);
        if (Number.isFinite(p.currentValue)) dept.start += Number(p.currentValue);
        else dept.startKnown = false;
        if (Number.isFinite(p.proposedValue)) dept.proposed += Number(p.proposedValue);
      }
      row.departments = [...byDept.values()].sort((a, b) =>
        Math.abs(b.proposed - (b.startKnown ? b.start : 0)) - Math.abs(a.proposed - (a.startKnown ? a.start : 0)));
    }

    return [...byMonth.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [proposals]);

  if (months.length === 0) return null;

  const totals = months.reduce((acc, r) => ({
    start: acc.start + (r.startKnown ? r.start : 0),
    proposed: acc.proposed + r.proposed,
    // A total change is only honest when every month knew where it started.
    startKnown: acc.startKnown && r.startKnown
  }), { start: 0, proposed: 0, startKnown: true });

  const groupState = (items) => {
    const on = items.filter(p => selected[p.id]).length;
    return on === 0 ? 'off' : on === items.length ? 'on' : 'some';
  };

  const Row = ({ label, group, state, delta, startKnown, start, proposed, onExpand, expanded, detail }) => (
    <div className={`wf-ledger-row ${detail ? 'wf-ledger-detail' : ''} ${state === 'off' ? 'is-off' : ''}`}>
      <label className="wf-ledger-period">
        <input
          type="checkbox"
          checked={state === 'on'}
          ref={el => { if (el) el.indeterminate = state === 'some'; }}
          disabled={disabled}
          onChange={() => onToggleMany(group.map(p => p.id), state !== 'on')}
        />
        <span>{label}</span>
        {onExpand && (
          <button type="button" className="wf-ledger-expand" aria-expanded={expanded} onClick={onExpand}>
            {expanded ? 'Hide' : `${group.length} line${group.length === 1 ? '' : 's'}`}
          </button>
        )}
      </label>
      <span className="wf-num">{startKnown ? amount(start) : '—'}</span>
      <span className="wf-num">{amount(proposed)}</span>
      <span className={`wf-num ${delta > 0 ? 'is-up' : delta < 0 ? 'is-down' : ''}`}>{variance(delta)}</span>
    </div>
  );

  return (
    <div className="wf-ledger">
      <div className="wf-ledger-head">
        <span>Month</span>
        <span>Start</span>
        <span>Proposal</span>
        <span>Change</span>
      </div>

      {months.map(row => {
        const isOpen = openMonth === row.key;
        return (
          <React.Fragment key={row.key}>
            <Row
              label={`${MONTH_ABBR[row.month - 1]} ${row.year}`}
              group={row.items}
              state={groupState(row.items)}
              startKnown={row.startKnown}
              start={row.start}
              proposed={row.proposed}
              delta={row.startKnown ? row.proposed - row.start : null}
              expanded={isOpen}
              onExpand={() => setOpenMonth(isOpen ? null : row.key)}
            />
            {isOpen && row.departments.map(dept => (
              <Row
                key={`${row.key}:${dept.name}`}
                detail
                label={dept.name}
                group={dept.items}
                state={groupState(dept.items)}
                startKnown={dept.startKnown}
                start={dept.start}
                proposed={dept.proposed}
                delta={dept.startKnown ? dept.proposed - dept.start : null}
              />
            ))}
          </React.Fragment>
        );
      })}

      <div className="wf-ledger-rule" />
      <div className="wf-ledger-row wf-ledger-total">
        <span className="wf-ledger-period">Total</span>
        <span className="wf-num">{totals.startKnown ? amount(totals.start) : '—'}</span>
        <span className="wf-num">{amount(totals.proposed)}</span>
        <span className="wf-num">{totals.startKnown ? variance(totals.proposed - totals.start) : '—'}</span>
      </div>
    </div>
  );
}

/**
 * The before → after line, shared by every proposal kind.
 *
 * An approver must be able to read what will happen without decoding a tool
 * call: a value change reads "1,200 → 1,450", a mapping reads
 * "not mapped → Rooms", a creation reads "will be created", a deletion reads
 * "will be deleted". Labels (strings) win over values (numbers) when both are
 * present, because a named target is more legible than an id.
 */
function ProposalChange({ proposal }) {
  const verb = proposal.changeVerb;
  const from = proposal.currentLabel != null
    ? proposal.currentLabel
    : proposal.currentValue != null ? Number(proposal.currentValue).toLocaleString() : null;
  const to = proposal.proposedLabel != null
    ? proposal.proposedLabel
    : proposal.proposedValue != null ? Number(proposal.proposedValue).toLocaleString() : null;

  if (from == null && to == null) return null;

  return (
    <div className={`wf-proposal-change wf-change--${verb || 'update'}`}>
      {from != null && <span className="wf-change-from">{from}</span>}
      {from != null && to != null && <span className="wf-change-arrow" aria-label="becomes">→</span>}
      {to != null && (
        <span className={`wf-change-to${verb === 'delete' ? ' is-removal' : ''}`}>{to}</span>
      )}
    </div>
  );
}

function DailyRoomsPreview({ target }) {
  const segments = (target.segments || []).filter(s => Array.isArray(s.dailyValues));
  if (!segments.length) return null;
  return (
    <details className="wf-daily-import">
      <summary>Review daily values for {segments.length} stat accounts</summary>
      {segments.map(segment => (
        <details key={segment.statAccountId}>
          <summary>{segment.accountName || segment.accountNumber} · {segment.rn.toLocaleString()} RN · {amount(segment.revenue)} revenue</summary>
          {(target.estimates || []).some(e => e.statAccountId === segment.statAccountId) && (
            <p>Revenue is from the file. RN or daily distribution includes estimates described below.</p>
          )}
          <table>
            <thead><tr><th>Day</th><th>RN</th><th>ADR</th><th>Revenue</th></tr></thead>
            <tbody>{segment.dailyValues.map(day => (
              <tr key={day.day}><td>{day.day}</td><td>{day.rn.toLocaleString()}</td>
                <td>{day.adr == null ? '—' : Number(day.adr).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td>{Number(day.revenue).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
            ))}</tbody>
          </table>
        </details>
      ))}
    </details>
  );
}

function ProposalRow({ proposal, selected, onToggle, disabled, hideRationale = false, hideChange = false, onRevert = null, reverting = false }) {
  const t = proposal.target || {};
  const terminal = ['applied', 'rejected', 'superseded', 'reverted'].includes(proposal.state);
  const revertible = typeof onRevert === 'function' && isRevertible(proposal);
  return (
    <div className={`wf-proposal wf-proposal--${proposal.state}`}>
      <label className="wf-proposal-select">
        <input
          type="checkbox"
          checked={!!selected}
          disabled={disabled || terminal}
          onChange={() => onToggle(proposal.id)}
        />
      </label>
      <div className="wf-proposal-body">
        <div className="wf-proposal-head">
          <span className="wf-proposal-title">{proposal.title}</span>
          <span className="wf-proposal-head-right">
            {revertible && (
              <button
                type="button"
                className="wf-revert"
                disabled={disabled || reverting}
                onClick={() => onRevert([proposal.id])}
                title="Undo this change (runs the inverse of what was applied)"
              >
                {reverting ? 'Reverting…' : 'Revert'}
              </button>
            )}
            <span className={`wf-badge wf-badge--${proposal.state}`}>{STATE_LABEL[proposal.state] || proposal.state}</span>
          </span>
        </div>
        {t.subjectCode && <div className="wf-proposal-code">{t.subjectCode}</div>}
        {!t.subjectCode && formatTarget(proposal) && <div className="wf-proposal-target">{formatTarget(proposal)}</div>}
        {t.comparison && <BudgetAccountComparison comparison={t.comparison} />}
        {t.amountsByMonth && (
          <div className="wf-proposal-change">
            {Object.entries(t.amountsByMonth).map(([m, v]) => `${MONTH_SHORT[Number(m) - 1] || m} ${Number(v).toLocaleString()}`).join(' · ')}
          </div>
        )}
        {t.driverType && (
          <div className="wf-proposal-change">
            Driver → <strong>{t.driverType}</strong>
            {t.driverInput != null && <> (input {t.driverInput})</>}
            {/* A Labor Config driver carries no single input — the per-position
                payload IS the rate. Rendering nothing would read as a driver
                with no setting, which is exactly what an empty payload would
                (silently) produce. */}
            {Array.isArray(t.laborPositionConfigs) && t.laborPositionConfigs.length > 0 && (
              <> ({t.laborPositionConfigs.length} position{t.laborPositionConfigs.length === 1 ? '' : 's'})</>
            )}
            {/* The pools a percentage is struck on, and the feed a Room
                Forecast reads, are the whole decision — "% of Selected
                Revenue" or "Room Forecast" alone says nothing reviewable. */}
            {Array.isArray(t.selectedDepartments) && (
              <> of {t.selectedDepartments.length ? t.selectedDepartments.join(' + ') : 'total revenue'}</>
            )}
            {Array.isArray(t.sourceChartOfAccountIds) && t.sourceChartOfAccountIds.length > 0 && (
              <> of {t.sourceChartOfAccountIds.length} account{t.sourceChartOfAccountIds.length === 1 ? '' : 's'}</>
            )}
            {Array.isArray(t.zeroBaseDriverLayers) && t.zeroBaseDriverLayers.length > 0 && (
              <> ({t.zeroBaseDriverLayers.map(l => `${l.type} ${l.value}`).join(', ')})</>
            )}
            {t.forecastSource && <> from <strong>{t.forecastSource}</strong></>}
          </div>
        )}
        {/* An override REPLACES a value; an adjustment is posted BESIDE the
            line it corrects. Rendering both as "current → proposed" reads as
            though the line becomes the adjustment amount, which is a different
            and much larger number. Additive kinds show the entry and what it
            does to the balance instead. */}
        {hideChange ? null : ADDITIVE_KINDS.has(proposal.kind) && proposal.proposedValue != null ? (
          <div className="wf-proposal-change">
            Correcting entry <strong>{signed(proposal.proposedValue)}</strong>
            {proposal.expectedImpact?.dayNetBefore != null && (
              <> · day net {Number(proposal.expectedImpact.dayNetBefore).toLocaleString()} → <strong>
                {Number(proposal.expectedImpact.dayNetAfter ?? 0).toLocaleString()}</strong></>
            )}
          </div>
        ) : (
          <ProposalChange proposal={proposal} />
        )}
        {!hideRationale && <div className="wf-proposal-reason">{proposal.rationale}</div>}
        {proposal.kind === 'forecast_revenue_stat_month' && <DailyRoomsPreview target={t} />}
        {proposal.limitations?.length > 0 && (
          <div className="wf-proposal-limits">{proposal.limitations.join(' ')}</div>
        )}
        {proposal.conflict && (
          <div className="wf-proposal-conflict">
            Not applied — {proposal.conflict.reason} Re-run the analysis to see the current state.
          </div>
        )}
        {proposal.applyError && !proposal.conflict && (
          <div className="wf-proposal-conflict">{proposal.applyError}</div>
        )}
        {proposal.revertError && proposal.state === 'applied' && (
          <div className="wf-proposal-conflict">Revert failed — {proposal.revertError}</div>
        )}
      </div>
    </div>
  );
}

function WorkflowDetails({ run, evidence, proposals, rejected, journalArtifact }) {
  const metadata = [
    run.run?.workflowRef,
    run.run?.asOf ? `as of ${run.run.asOf}` : null,
    run.run?.dataSource === 'report' ? 'report-backed' : null,
  ].filter(Boolean).join(' · ');
  const proposalLimitations = [...new Set(proposals.flatMap(p => p.limitations || []))];

  return (
    <div className="wf-details">
      {journalArtifact && <JournalImbalanceDetails artifact={journalArtifact} />}
      {journalArtifact && proposalLimitations.length > 0 && (
        <div>
          <div className="wf-run-section-title">Correction policy</div>
          <div className="wf-rejected-row">{proposalLimitations.join(' ')}</div>
        </div>
      )}
      {metadata && <div className="wf-run-meta-detail">{metadata}</div>}
      {run.openQuestions?.length > 0 && (
        <div className="wf-run-questions">
          <div className="wf-run-section-title">Open questions</div>
          <ul>{run.openQuestions.map((q, i) => <li key={i}>{q}</li>)}</ul>
        </div>
      )}
      {evidence.length > 0 && (
        <div>
          <div className="wf-run-section-title">Evidence</div>
          {evidence.map(e => (
            <div key={e.id} className={`wf-evidence-row ${e.ok ? '' : 'wf-evidence-row--failed'}`}>
              <span className="wf-evidence-id">{e.id}</span>
              <span className="wf-evidence-purpose">{e.purpose}</span>
              <span className="wf-evidence-meta">
                {e.tool}
                {e.asOf && <> · as of {e.asOf}</>}
                {!e.ok && <> · unavailable</>}
              </span>
            </div>
          ))}
        </div>
      )}
      {proposals.some(p => p.candidateId || p.confidence) && (
        <div>
          <div className="wf-run-section-title">Provenance</div>
          {proposals.map(p => (
            <div key={p.id} className="wf-evidence-row">
              <span className="wf-evidence-id">{p.id}</span>
              <span className="wf-evidence-purpose">
                {CONFIDENCE_LABEL[p.confidence] || p.confidence}
                {p.evidenceIds?.length > 0 && <> · cites {p.evidenceIds.join(', ')}</>}
              </span>
              {p.candidateId && <span className="wf-evidence-meta">{p.candidateId}</span>}
            </div>
          ))}
        </div>
      )}
      {rejected.length > 0 && (
        <div>
          <div className="wf-run-section-title">Not shown ({rejected.length})</div>
          {rejected.map(r => (
            <div key={r.id} className="wf-rejected-row"><strong>{r.title}</strong>: {r.errors.join('; ')}</div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Human titles per artifact type. The run supplies its own; this is a fallback. */
/**
 * The narrative's first sentence is the headline ("answer first"); whatever
 * follows becomes the collapsed "What I found" section. Decimal points and
 * abbreviations do not split because a sentence end needs trailing space.
 */
function splitNarrative(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return { headline: '', rest: '' };
  const m = t.match(/^(.{20,}?[.!?])(?:\s+|$)([\s\S]*)$/);
  if (!m) return { headline: t, rest: '' };
  return { headline: m[1].trim(), rest: m[2].trim() };
}

const money0 = (v) => `${v < 0 ? '-' : ''}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;
const signedMoney0 = (v) => `${v > 0 ? '+' : v < 0 ? '-' : ''}$${Math.abs(Math.round(v)).toLocaleString('en-US')}`;

/**
 * Signal strip for a run without a proof line: the figures a reviewer wants
 * before the rows — where the total starts, where it lands, the change, and
 * how many periods/lines move. Returns null when there is nothing numeric.
 */
function deriveProposalSignals(proposals) {
  const numeric = proposals.filter(p => Number.isFinite(Number(p.currentValue)) && Number.isFinite(Number(p.proposedValue)));
  if (numeric.length < 1) return null;
  const start = numeric.reduce((s, p) => s + Number(p.currentValue), 0);
  const proposed = numeric.reduce((s, p) => s + Number(p.proposedValue), 0);
  const delta = proposed - start;
  const months = new Set(numeric.map(p => `${p.target?.year}-${p.target?.month}`).filter(k => !k.startsWith('undefined')));
  const unit = numeric.some(p => /rate|pct|percent|%/i.test(p.kind || '') || Math.abs(Number(p.proposedValue)) < 5) ? 'raw' : 'money';
  const fmt = unit === 'money' ? money0 : (v) => Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 });
  const sfmt = unit === 'money' ? signedMoney0 : (v) => `${v > 0 ? '+' : ''}${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  const pct = start !== 0 ? `${delta >= 0 ? '+' : ''}${((delta / Math.abs(start)) * 100).toFixed(1)}% vs current` : 'vs current';
  return [
    { label: 'Current', value: fmt(start), sub: months.size > 1 ? `${months.size} months` : 'as proposed today' },
    { label: 'Proposed', value: fmt(proposed), sub: pct, tone: 'accent' },
    { label: 'Change', value: sfmt(delta), sub: `${numeric.length} line${numeric.length === 1 ? '' : 's'} move`, tone: delta > 0 ? 'positive' : delta < 0 ? 'negative' : 'muted' },
  ];
}

/** The action card's status pill from the proposal states. */
function proposalsPill(proposals, busy) {
  if (!proposals.length) return null;
  const states = proposals.map(p => p.state);
  const every = (s) => states.every(x => x === s);
  if (busy === 'apply') return { state: 'busy', label: 'Applying' };
  if (busy === 'revert') return { state: 'busy', label: 'Reverting' };
  if (every('applied')) return { state: 'done', label: 'Applied' };
  if (every('reverted')) return { state: 'muted', label: 'Reverted' };
  if (every('rejected')) return { state: 'muted', label: 'Rejected' };
  if (every('deferred')) return { state: 'muted', label: 'Deferred' };
  if (every('superseded')) return { state: 'muted', label: 'Superseded' };
  if (states.some(s => s === 'applying')) return { state: 'busy', label: 'Applying' };
  if (states.every(s => ['applied', 'failed', 'reverted', 'rejected', 'superseded'].includes(s)) && states.includes('failed')) return { state: 'error', label: 'Failed' };
  if (states.every(s => ['applied', 'reverted', 'rejected', 'superseded', 'deferred'].includes(s))) return { state: 'done', label: 'Decided' };
  return { state: 'pending', label: 'Needs your approval' };
}

const WORKFLOW_TITLES = {
  'forecast_accuracy_review.v1': 'Forecast accuracy review',
  'monthly_landing.v1': 'Month-end landing',
  'revenue_reforecast.v1': 'Revenue reforecast review',
  'revenue_budget.v1': 'Rooms revenue budget',
  'revenue_budget.v2': 'Operating budget',
  'room_revenue_import.v1': 'Daily Rooms import',
  'rooms_expense_audit.v1': 'Rooms expense audit',
  'monthly_close_review.v1': 'Monthly close review',
};

const WorkflowRunCard = ({ initialRun, currentOrgId, onRunUpdated, contextLabel = null, defaultShowDetails = false }) => {
  const [run, setRun] = useState(initialRun);
  const [selected, setSelected] = useState(() => {
    const initial = {};
    for (const p of initialRun?.proposals || []) {
      if (p.state === 'draft' || p.state === 'approved' || isRetryableApplying(p)) initial[p.id] = true;
    }
    return initial;
  });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(defaultShowDetails);
  const { notifyDataChanged } = useAssistantContext();
  const [applyResult, setApplyResult] = useState(null);
  const [revertingIds, setRevertingIds] = useState([]);
  const [automationOfferDismissed, setAutomationOfferDismissed] = useState(false);
  const [watchSetupOpen, setWatchSetupOpen] = useState(false);

  const proposals = run?.proposals || [];
  const artifacts = run?.artifacts || [];
  // An analysis-only run has nothing to approve. Rendering decision controls
  // for it would offer the user a button that cannot do anything.
  const isAnalysisOnly = artifacts.length > 0 && proposals.length === 0;
  const selectedIds = proposals.filter(p => selected[p.id]).map(p => p.id);
  const decisionIds = proposals
    .filter(p => selected[p.id] && ['draft', 'reviewed', 'approved', 'deferred', 'failed'].includes(p.state))
    .map(p => p.id);
  const applicableIds = proposals
    .filter(p => selected[p.id] && (p.state === 'approved' || isRetryableApplying(p)))
    .map(p => p.id);
  const runId = run?.run?.workflowRunId;
  // Durable runs carry their own organization scope. Prefer the caller's
  // current scope, but retain the run scope as a reload/history fallback so
  // review controls never issue an unscoped decision request.
  const effectiveOrgId = currentOrgId || run?.run?.orgId;
  // When the proof line carries the figures, the per-proposal change row would
  // restate them one line lower.
  const proofCandidates = proposals.filter(p => Number.isFinite(p.expectedImpact?.dayNetBefore));
  const hasProof = proofCandidates.length === 1 || (
    proposals.length > 1
    && proofCandidates.length === proposals.length
    && proposals.every(p => String(p.kind || '').startsWith('journal_adjustment'))
  );

  const toggle = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const toggleMany = (ids, on) => setSelected(prev => {
    const next = { ...prev };
    for (const id of ids) next[id] = on;
    return next;
  });

  // A period ledger only makes sense when the proposals ARE periods. Anything
  // without a year and month (a driver swap, a journal correction) still reads
  // better as rows, so it keeps them.
  const isPeriodic = proposals.length > 2
    && proposals.every(p => Number.isInteger(Number(p.target?.year)) && Number.isInteger(Number(p.target?.month)));
  const isAdvisory = proposals.length > 0 && proposals.every(p => ADVISORY_KINDS.has(p.kind));

  const patchProposals = (updater) => {
    setRun(prev => {
      const next = { ...prev, proposals: prev.proposals.map(updater) };
      if (onRunUpdated) onRunUpdated(next);
      return next;
    });
  };

  const decide = async (action) => {
    if (decisionIds.length === 0 || busy) return [];
    setBusy(action);
    setError(null);
    try {
      const result = await decideWorkflowProposals({ runId, currentOrgId: effectiveOrgId, action, proposalIds: decisionIds });
      const changed = new Set(result.changed || []);
      patchProposals(p => (changed.has(p.id) ? { ...p, state: action } : p));
      return [...changed];
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
      return [];
    } finally {
      setBusy(null);
    }
  };

  /**
   * One click: approve the selected proposals, then write them. A separate
   * "Apply" step after "Approve" was two confirmations for one decision; the
   * undo path is Revert on the applied row, not a pause before applying.
   * Advisory kinds still only approve (there is nothing to write).
   */
  const approveAndApply = async () => {
    const approved = await decide('approved');
    if (isAdvisory || approved.length === 0) return;
    const ids = proposals
      .filter(p => approved.includes(p.id) || (selected[p.id] && (p.state === 'approved' || isRetryableApplying(p))))
      .map(p => p.id);
    await applyIds([...new Set(ids)]);
  };

  const applyApproved = () => applyIds(applicableIds);

  const applyIds = async (ids) => {
    if (!ids || ids.length === 0 || busy) return;
    setBusy('apply');
    setError(null);
    try {
      const result = await applyWorkflowProposals({ runId, currentOrgId: effectiveOrgId, proposalIds: ids });
      setApplyResult(result);
      const byId = new Map((result.results || []).map(r => [r.id, r]));
      patchProposals(p => {
        const r = byId.get(p.id);
        if (!r) return p;
        return {
          ...p,
          state: r.state || (r.ok ? 'applied' : 'failed'),
          applyError: r.ok ? null : r.message,
          conflict: r.stale ? { reason: r.message } : p.conflict,
          revertible: r.ok ? !!r.revertible : p.revertible,
        };
      });

      // Tell the page underneath to re-read itself. Only for proposals that
      // actually wrote: announcing a change for a run where everything was
      // refused would make the page refetch and show the same figures, which
      // reads as "the apply did nothing" even when nothing was supposed to
      // happen. Fired AFTER the response, never optimistically — a refetch
      // that races the write re-reads the old value and looks like a failure.
      const written = (result.results || []).filter(r => r.ok);
      if (written.length > 0) {
        const applied = proposals.filter(p => written.some(r => r.id === p.id));
        // One 18-month run can write more than one annual version. Notify each
        // target version separately so the page showing that version refreshes
        // immediately and sibling years do not refetch unnecessarily.
        const byVersion = new Map();
        for (const proposal of applied) {
          const versionId = proposal.target?.versionId ?? run?.run?.forecastVersionId ?? null;
          const key = versionId == null ? 'none' : String(versionId);
          if (!byVersion.has(key)) byVersion.set(key, { versionId, proposals: [] });
          byVersion.get(key).proposals.push(proposal);
        }
        for (const { versionId, proposals: versionProposals } of byVersion.values()) {
          notifyDataChanged({
            kinds: [...new Set(versionProposals.map(p => p.kind))],
            locationId: versionProposals.find(p => p.target?.locationId)?.target?.locationId
              ?? run?.run?.locationId ?? null,
            dates: [...new Set(versionProposals.map(p => p.target?.effectiveDate).filter(Boolean))],
            versionId,
            workflowRunId: runId,
          });
        }
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(null);
    }
  };

  const revertIds = async (ids) => {
    if (!ids || ids.length === 0 || busy) return;
    setBusy('revert');
    setRevertingIds(ids);
    setError(null);
    try {
      const result = await revertWorkflowProposals({ runId, currentOrgId: effectiveOrgId, proposalIds: ids });
      const byId = new Map((result.results || []).map(r => [r.id, r]));
      const skipped = new Map((result.skipped || []).map(r => [r.id, r]));
      patchProposals(p => {
        const r = byId.get(p.id);
        if (r) {
          return r.ok
            ? { ...p, state: r.state || 'reverted', revertError: null, revertible: false }
            : { ...p, revertError: r.message };
        }
        const sk = skipped.get(p.id);
        return sk ? { ...p, revertError: sk.reason } : p;
      });
      const undone = (result.results || []).filter(r => r.ok);
      if (undone.length > 0) {
        const reverted = proposals.filter(p => undone.some(r => r.id === p.id));
        notifyDataChanged({
          kinds: [...new Set(reverted.map(p => p.kind))],
          locationId: reverted.find(p => p.target?.locationId)?.target?.locationId ?? run?.run?.locationId ?? null,
          dates: [],
          versionId: run?.run?.forecastVersionId ?? null,
          workflowRunId: runId,
        });
      }
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(null);
      setRevertingIds([]);
    }
  };

  /**
   * Answer a question the run asked.
   *
   * The answer re-parameterizes the SAME workflow rather than being typed back
   * into chat for a model to re-interpret: picking "2027 Budget Draft" runs
   * the identical review pinned to that version. A question worth asking is
   * worth being able to answer in one click.
   */
  const answerPrompt = async (prompt, option) => {
    if (busy || !run?.run?.workflowRef || !run?.run?.locationId) return;
    setBusy(`prompt:${prompt.id}`);
    setError(null);
    try {
      const result = await runWorkflow({
        workflowRef: run.run.workflowRef,
        hotelId: run.run.locationId,
        currentOrgId: effectiveOrgId,
        versionId: option.value?.versionId ?? null,
        year: option.value?.year ?? null,
        ...(option.value?.params ? { params: option.value.params } : {})
      });
      setRun(result);
      setSelected({});
      if (onRunUpdated) onRunUpdated(result);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setBusy(null);
    }
  };

  // The watch is built from the run that produced this offer — same workflow
  // ref, same version — rather than from a sentence the model has to
  // re-interpret. What the user still chooses is cadence and threshold.
  const watchTarget = useMemo(() => ({
    workflowRef: run?.run?.workflowRef || null,
    hotelId: run?.run?.locationId || proposals.find(p => p.target?.locationId)?.target?.locationId || null,
    versionId: run?.run?.forecastVersionId ?? null,
    year: proposals.find(p => p.target?.year)?.target?.year ?? null,
    versionName: proposals.find(p => p.target?.versionName)?.target?.versionName ?? null,
    // A journal detector is delegated to ONE issue pattern — the shape of the
    // correction just reviewed — never to journal problems in general.
    issuePatternKey: (artifacts.find(a => a.type === 'journal_imbalance')?.data?.issuePatternKey) || null
  }), [run, proposals, artifacts]);

  if (!run) return null;

  const anyApproved = applicableIds.length > 0;
  const retryCount = proposals.filter(p => selected[p.id] && isRetryableApplying(p)).length;
  const revertibleIds = proposals.filter(isRevertible).map(p => p.id);
  // Selected rows still needing a decision. When none remain but approved
  // rows are selected (a prior session, a failed apply), the primary is Apply.
  const pendingCount = proposals.filter(p => selected[p.id] && ['draft', 'reviewed', 'deferred', 'failed'].includes(p.state)).length;
  const evidence = run.evidence || [];
  const rejected = run.rejectedProposals || [];
  const journalArtifact = artifacts.find(a => a.type === 'journal_imbalance') || null;
  const journalProposal = proposals.length === 1 && proposals[0].kind === 'journal_adjustment'
    ? proposals[0]
    : null;
  const isCompactJournal = !!journalArtifact && !!journalProposal;
  const sourceEntry = journalArtifact?.data?.sourceDocument?.explanation?.entries?.[0];
  const journalCause = journalArtifact?.data?.sourceDocument?.explanation?.cause
    || journalArtifact?.data?.causes?.[0]?.cause;
  const sourceEntryName = sourceEntry?.description || sourceEntry?.name;
  const displayTitle = isCompactJournal && sourceEntryName && ['wrong_side', 'source_side_error', 'sign_flip'].includes(journalCause)
    ? `${sourceEntryName} was posted on the wrong side`
    : artifacts[0]?.title
      || WORKFLOW_TITLES[run.run?.workflowRef]
      || run.run?.workflowRef
      || 'Workflow run';
  const displayContext = compactContextLabel(
    contextLabel || run.pageContext?.label || run.run?.contextLabel || ''
  );
  const journalTerminal = ['applied', 'rejected', 'deferred', 'failed', 'superseded'].includes(journalProposal?.state);
  const journalDetectorEligible = !!journalArtifact?.data?.issuePatternKey
    && proposals.length > 0
    && proposals.every(proposal => proposal.state === 'applied');

  // ── Pattern anatomy ──────────────────────────────────────────────────
  // 1 · Answer first: the narrative's first sentence is the headline; the
  //     rest becomes a collapsed "What I found" section. A compact journal
  //     card leads with its diagnosis instead.
  const { headline: narrativeHeadline, rest: narrativeRest } = splitNarrative(run.narrative);
  const headline = isCompactJournal ? displayTitle : (narrativeHeadline || displayTitle);
  // 2 · Caveats before numbers: open questions + artifact limitations.
  // An open question that the run also asks as a prompt (with answer buttons)
  // is not repeated as a caveat.
  const promptQuestions = new Set((run.prompts || []).map(pr => String(pr.question || '').trim()));
  const caveats = [
    ...(run.openQuestions || []).filter(q => !promptQuestions.has(String(q || '').trim())).slice(0, 2),
    ...(isCompactJournal ? [] : [...new Set(artifacts.flatMap(a => a.data?.limitations || []))].slice(0, 2)),
  ];
  // 3 · Signals: the ProofLine (journal) or a derived strip from the proposals.
  const signals = hasProof ? null : deriveProposalSignals(proposals);
  // 5 · Action: the decision state drives the pill.
  const actionPill = proposalsPill(proposals, busy);
  const applyKind = proposals.some(p => String(p.kind || '').startsWith('forecast_')) ? 'trend'
    : proposals.some(p => String(p.kind || '').startsWith('journal_')) ? 'check'
      : 'check';
  const actionTitle = isCompactJournal
    ? 'Post the correcting entry'
    : isAdvisory
      ? `${proposals.length} item${proposals.length === 1 ? '' : 's'} for review`
      : proposals.some(p => String(p.kind || '').startsWith('forecast_'))
        ? `Update the forecast · ${proposals.length} change${proposals.length === 1 ? '' : 's'}`
        : proposals.every(p => String(p.kind || '').startsWith('journal_'))
          ? `Post ${proposals.length} correcting entr${proposals.length === 1 ? 'y' : 'ies'}`
          : proposals.every(p => p.kind === 'api_mutation')
            ? `Apply ${proposals.length} configuration change${proposals.length === 1 ? '' : 's'}`
            : `${proposals.length} proposed change${proposals.length === 1 ? '' : 's'}`;
  const failedErrors = [...new Set(proposals.filter(p => p.state === 'failed' && p.applyError).map(p => p.applyError))];
  const actionDone = applyResult && proposals.length > 0 && proposals.every(p => ['applied', 'rejected', 'reverted', 'superseded'].includes(p.state))
    ? {
        tone: applyResult.failed > 0 ? 'error' : 'success',
        text: `${applyResult.applied} applied${applyResult.superseded > 0 ? `, ${applyResult.superseded} blocked as out of date` : ''}${applyResult.failed > 0 ? `, ${applyResult.failed} failed` : ''}.`,
      }
    : null;
  const applyNote = applyResult && !actionDone
    ? `${applyResult.applied} applied${applyResult.superseded > 0 ? `, ${applyResult.superseded} blocked as out of date` : ''}${applyResult.failed > 0 ? `, ${applyResult.failed} failed` : ''}`
    : null;

  const sourcesPreview = [
    evidence.length ? `${evidence.length} source${evidence.length === 1 ? '' : 's'}` : null,
    run.run?.asOf ? `as of ${run.run.asOf}` : null,
    run.run?.dataSource === 'report' ? 'report-backed' : null,
    rejected.length ? `${rejected.length} not shown` : null,
  ].filter(Boolean).join(' · ');

  const watchOffer = ({ title, body, primary, onPrimary, onDismiss, dismiss }) => (
    <ActionCard
      icon="clock"
      title={title}
      className="wf-run-result"
      note={body}
      footer={(
        <>
          <Button variant="primary" onClick={onPrimary}>{primary}</Button>
          <Button variant="ghost" onClick={onDismiss}>{dismiss}</Button>
        </>
      )}
    />
  );

  const journalWatch = journalDetectorEligible && !automationOfferDismissed && (
    watchSetupOpen ? (
      <WatchSetup
        {...watchTarget}
        workflowTitle="this journal issue"
        currentOrgId={effectiveOrgId}
        onCancel={() => { setWatchSetupOpen(false); setAutomationOfferDismissed(true); }}
      />
    ) : watchOffer({
      title: 'Watch for this issue at this location?',
      body: 'I can monitor this exact journal correction pattern at this property and notify you when one needs approval. Different issue patterns remain outside the delegated repair scope, other properties are never inspected, and nothing is fixed automatically.',
      primary: 'Watch for it',
      onPrimary: () => setWatchSetupOpen(true),
      dismiss: 'Not now',
      onDismiss: () => setAutomationOfferDismissed(true),
    })
  );

  const forecastWatch = applyResult?.applied > 0
    && proposals.some(p => [
      'forecast_adjustment',
      'forecast_override',
      'forecast_driver',
      'department_driver',
      // Native Rooms plans write room nights, ADR, Rooms revenue and
      // occupied rooms atomically. They are still forecast changes,
      // and are the primary successful handoff into a reforecast
      // watch.
      'forecast_revenue_stat_month',
    ].includes(p.kind))
    && !automationOfferDismissed && watchTarget.workflowRef && watchTarget.hotelId && (
      watchSetupOpen ? (
        <WatchSetup
          {...watchTarget}
          workflowTitle={run?.run?.workflowRef}
          currentOrgId={effectiveOrgId}
          onCreated={() => setWatchSetupOpen(true)}
          onCancel={() => { setWatchSetupOpen(false); setAutomationOfferDismissed(true); }}
        />
      ) : watchOffer({
        title: 'Keep watching this forecast?',
        body: 'I can re-run this exact review on a schedule and email you only when a material, evidence-backed change needs approval. Nothing is ever applied automatically.',
        primary: 'Set up a watch',
        onPrimary: () => setWatchSetupOpen(true),
        dismiss: 'No thanks',
        onDismiss: () => setAutomationOfferDismissed(true),
      })
    );

  const sourcesSection = (
    <Section
      id="wf-sources"
      label="Sources & assumptions"
      count={evidence.length || null}
      preview={sourcesPreview}
      open={showDetails}
      onToggle={setShowDetails}
      bodyClassName="is-sources"
    >
      <WorkflowDetails
        run={run}
        evidence={evidence}
        proposals={proposals}
        rejected={rejected}
        journalArtifact={isCompactJournal ? journalArtifact : null}
      />
      {isCompactJournal && !journalTerminal && (
        <div className="wf-secondary-actions">
          <Button size="sm" disabled={!!busy} onClick={() => decide('rejected')}>Reject</Button>
          <Button size="sm" disabled={!!busy} onClick={() => decide('deferred')}>Defer</Button>
        </div>
      )}
    </Section>
  );

  // ── Action footer per mode ───────────────────────────────────────────
  let actionFooter = null;
  let actionFooterMeta = null;
  if (isCompactJournal) {
    actionFooter = (
      <>
        {journalProposal.state === 'draft' && (
          <Button variant="primary" disabled={!!busy || selectedIds.length === 0} onClick={() => decide('approved')}>
            {busy === 'approved' ? 'Approving…' : 'Approve correction'}
          </Button>
        )}
        {journalProposal.state === 'approved' && (
          <Button variant="primary" disabled={!!busy} onClick={applyApproved}>
            {busy === 'apply' ? 'Applying…' : 'Apply correction'}
          </Button>
        )}
        {journalProposal.state === 'applying' && (isRetryableApplying(journalProposal)
          ? (
            <Button variant="primary" disabled={!!busy} onClick={applyApproved}>
              {busy === 'apply' ? 'Retrying…' : 'Retry correction'}
            </Button>
          )
          : <span className="wf-action-state">Applying…</span>)}
        {journalProposal.state === 'applied' && <span className="wf-action-state wf-action-state--done">Applied</span>}
        {journalProposal.state === 'deferred' && <span className="wf-action-state">Deferred</span>}
        {journalProposal.state === 'rejected' && <span className="wf-action-state">Rejected</span>}
      </>
    );
  } else if (proposals.length > 0) {
    actionFooter = (
      <>
        {/* Approve writes in the same click. The undo is Revert on the
            applied row, so a second "Apply" confirmation buys nothing. */}
        {(pendingCount > 0 || !anyApproved || isAdvisory) && (
          <Button
            variant={decisionIds.length > 0 ? 'primary' : 'secondary'}
            className={decisionIds.length > 0 ? 'wf-primary' : ''}
            disabled={!!busy || decisionIds.length === 0}
            onClick={approveAndApply}
            title={isAdvisory ? 'Mark these for review' : 'Approve and apply the selected changes now'}
          >
            {busy === 'approved' ? 'Approving…'
              : busy === 'apply' ? 'Applying…'
              : `${isAdvisory ? 'Approve for review' : proposals.some(p => selected[p.id] && p.state === 'failed') ? 'Retry' : 'Approve'} ${decisionIds.length}`}
          </Button>
        )}
        {/* Only for rows approved earlier but never written (a prior
            session, a failed apply): the normal path never shows it. */}
        {!isAdvisory && anyApproved && pendingCount === 0 && (
          <Button variant="primary" className="wf-primary" disabled={!!busy} onClick={applyApproved} title="Write the approved changes">
            {busy === 'apply' ? 'Applying…' : retryCount > 0 ? `Retry ${retryCount} remaining` : `Apply ${applicableIds.length} approved`}
          </Button>
        )}
        <Button variant="ghost" disabled={!!busy || decisionIds.length === 0} onClick={() => decide('rejected')}>Reject</Button>
        <Button variant="ghost" disabled={!!busy || decisionIds.length === 0} onClick={() => decide('deferred')}>Defer</Button>
        {revertibleIds.length > 1 && (
          <Button
            variant="danger"
            className="wf-revert wf-revert--all"
            disabled={!!busy}
            onClick={() => revertIds(revertibleIds)}
            title="Undo every applied change on this card"
          >
            {busy === 'revert' ? 'Reverting…' : `Revert all ${revertibleIds.length}`}
          </Button>
        )}
      </>
    );
    actionFooterMeta = isAdvisory ? 'nothing is written' : selectedIds.length ? `${selectedIds.length} of ${proposals.length} selected` : null;
  }

  return (
    <div className="rp wf-run-card">
      {/* 1 · Answer first — the run's own title sits above as an eyebrow when
          the headline is a narrative sentence rather than the title itself. */}
      {headline !== displayTitle && <div className="rp-eyebrow">{displayTitle}</div>}
      <Headline>{headline}</Headline>

      {/* 2 · Caveats before numbers */}
      {caveats.map((c, i) => <Caveat key={`wf-caveat-${i}`} tone="info">{c}</Caveat>)}
      {failedErrors.map((message, i) => <Caveat key={`wf-apply-error-${i}`} tone="error">Not applied: {message}</Caveat>)}

      {/* A question the run could not answer for itself, put where a question
          belongs — above the result, not in a details drawer at the bottom.
          Where the answers are enumerable they are buttons, because a question
          that has to be answered by typing a version name back is a question
          most people abandon. */}
      {(run.prompts || []).length > 0 && (
        <div className="wf-run-prompts">
          {run.prompts.map(prompt => (
            <div className="wf-run-prompt" key={prompt.id}>
              <strong>{prompt.question}</strong>
              {prompt.detail && <div className="wf-run-prompt-detail">{prompt.detail}</div>}
              {prompt.options?.length > 0 && (
                <div className="wf-run-actions">
                  {prompt.options.map(option => (
                    <Button
                      key={option.label}
                      size="sm"
                      disabled={!!busy}
                      onClick={() => answerPrompt(prompt, option)}
                    >{busy === `prompt:${prompt.id}` ? 'Re-running…' : option.label}</Button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* 3 · One signal strip: the proof (journal) or the proposal figures. */}
      <ProofLine proposals={proposals} />
      {signals && <SignalStrip signals={signals} />}

      {/* The data view: typed artifact cards stay visible — they are the
          answer, not supporting detail. */}
      {artifacts.map(artifact => (
        <ArtifactRenderer key={artifact.id} artifact={artifact} />
      ))}

      {/* 4 · Sections, collapsed */}
      <Sections>
        {narrativeRest && !isCompactJournal && (
          <Section id="wf-narrative" label="What I found" preview={previewOf(narrativeRest)}>
            <div className="wf-run-narrative">{narrativeRest}</div>
          </Section>
        )}
        {sourcesSection}
      </Sections>

      {proposals.length === 0 && !isAnalysisOnly && (
        <div className="wf-run-empty wf-run-empty--verified">
          <strong>Nothing to approve</strong>
          <span>This run proposed no changes — the summary above says whether that is because the current setup held up or because something could not be evaluated. No notification was sent and nothing was written.</span>
        </div>
      )}

      {/* 5 · Action last, summarised */}
      {proposals.length > 0 && (
        <ActionCard
          icon={applyKind}
          title={actionTitle}
          sub={displayContext || null}
          pill={actionPill}
          note={applyNote}
          error={error}
          footer={actionFooter}
          footerMeta={actionFooterMeta}
          done={actionDone}
        >
          {isCompactJournal ? null : isPeriodic && !isAdvisory ? (
            <ProposalLedger
              proposals={proposals}
              selected={selected}
              onToggleMany={toggleMany}
              disabled={!!busy}
            />
          ) : (
            <div className={`wf-proposals${proposals.length > 5 ? ' wf-proposals--scroll' : ''}`}>
              {proposals.map(p => (
                <ProposalRow
                  key={p.id}
                  proposal={p}
                  selected={selected[p.id]}
                  onToggle={toggle}
                  disabled={!!busy}
                  // A driver CHANGE carries its evidence on the row (holdout
                  // error, the lean on closed months, the invoice) even when
                  // the review table is present; filling a driverless account
                  // (changeVerb 'create') needs only the driver.
                  hideRationale={artifacts.length > 0 && p.changeVerb === 'create'}
                  hideChange={hasProof}
                  onRevert={revertIds}
                  reverting={revertingIds.includes(p.id)}
                />
              ))}
            </div>
          )}
        </ActionCard>
      )}

      {journalWatch}
      {forecastWatch}
    </div>
  );
};

export default WorkflowRunCard;
